/**
 * What dated fields read and write (1.Y stage s1), behind one interface so a browser spec makes no
 * network request. The editors never call `api` or `appEvents` directly. Query keys sit under
 * `['temporal', …]`; NOW and the calendar are 1.O-1's (`useWhenContext`).
 */
import {
  type AppEventV1,
  type DocHitV1,
  type DocTitleV1,
  docsTitles,
  type EntityStateV1,
  type LinkCreateV1,
  type LinksQueryV1,
  type LinkTypeV1,
  type LinkUpdateV1,
  type LinkV1,
  linksCreate,
  linksList,
  linksTypes,
  linksUpdate,
  type PlacementCreateV1,
  type PlacementUpdateV1,
  type PlacementV1,
  placementsCreate,
  placementsList,
  placementsRemove,
  placementsUpdate,
  type StateCreateV1,
  type StateUpdateV1,
  searchPicker,
  statesCreate,
  statesList,
  statesUpdate,
} from '@codex/contracts';
import type { ApiClient } from '@codex/contracts/client';
import { createContext, useContext } from 'react';
import { api } from '../auth/client.js';
import { appEvents } from '../editor-services/events.js';

export type TemporalEventType = 'chrono.changed' | 'clock.changed' | 'links.changed';
export type TemporalEvent<T extends TemporalEventType> = Extract<AppEventV1, { type: T }>;

export interface TemporalApi {
  listStates(docId: string): Promise<EntityStateV1[]>;
  createState(docId: string, body: StateCreateV1): Promise<EntityStateV1>;
  updateState(docId: string, stateId: string, body: StateUpdateV1): Promise<EntityStateV1>;
  listLinks(docId: string, query: LinksQueryV1): Promise<LinkV1[]>;
  /** `sourceId` is the document whose path carries the link. */
  createLink(sourceId: string, body: LinkCreateV1): Promise<LinkV1>;
  updateLink(sourceId: string, linkId: string, body: LinkUpdateV1): Promise<LinkV1>;
  linkTypes(): Promise<LinkTypeV1[]>;
  titles(ids: readonly string[]): Promise<DocTitleV1[]>;
  /** Canon pages whose title matches. */
  searchCanon(q: string): Promise<DocHitV1[]>;
  listPlacements(docId: string): Promise<PlacementV1[]>;
  createPlacement(docId: string, body: PlacementCreateV1): Promise<PlacementV1>;
  updatePlacement(docId: string, id: string, body: PlacementUpdateV1): Promise<PlacementV1>;
  removePlacement(docId: string, id: string, baseRv: number): Promise<void>;
  /** Returns the unsubscribe. */
  subscribe<T extends TemporalEventType>(
    type: T,
    listener: (event: TemporalEvent<T>) => void,
  ): () => void;
}

export const temporalKeys = {
  states: (docId: string) => ['temporal', 'states', docId] as const,
  links: (docId: string) => ['temporal', 'links', docId] as const,
  linkTypes: ['temporal', 'link-types'] as const,
  titles: (ids: readonly string[]) => ['temporal', 'titles', ids.join(',')] as const,
  placements: (docId: string) => ['temporal', 'placements', docId] as const,
  picker: (q: string) => ['temporal', 'picker', q] as const,
};

export function createTemporalApi(client: ApiClient = api): TemporalApi {
  return {
    listStates: async (docId) => (await client(statesList, { params: { id: docId } })).items,
    createState: (docId, body) => client(statesCreate, { params: { id: docId }, body }),
    updateState: (docId, stateId, body) =>
      client(statesUpdate, { params: { id: docId, stateId }, body }),
    listLinks: async (docId, query) =>
      (await client(linksList, { params: { id: docId }, query })).items,
    createLink: async (sourceId, body) =>
      (await client(linksCreate, { params: { id: sourceId }, body })).link,
    updateLink: (sourceId, linkId, body) =>
      client(linksUpdate, { params: { id: sourceId, linkId }, body }),
    linkTypes: async () => (await client(linksTypes)).items,
    titles: async (ids) =>
      ids.length === 0 ? [] : (await client(docsTitles, { body: { ids: [...ids] } })).titles,
    searchCanon: async (q) => (await client(searchPicker, { query: { q, limit: 8 } })).canon,
    listPlacements: async (docId) =>
      (await client(placementsList, { params: { id: docId }, query: {} })).items,
    createPlacement: (docId, body) => client(placementsCreate, { params: { id: docId }, body }),
    updatePlacement: (docId, placementId, body) =>
      client(placementsUpdate, { params: { id: docId, placementId }, body }),
    removePlacement: async (docId, placementId, baseRv) => {
      await client(placementsRemove, { params: { id: docId, placementId }, query: { baseRv } });
    },
    subscribe: (type, listener) => appEvents.subscribe(type, listener),
  };
}

export const TemporalApiContext = createContext<TemporalApi | null>(null);

let shared: TemporalApi | null = null;

/** The provided services, or the real ones made once. */
export function useTemporalApi(): TemporalApi {
  const provided = useContext(TemporalApiContext);
  if (provided) return provided;
  shared ??= createTemporalApi();
  return shared;
}
